import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik } from "formik";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { meetingManagement } from "../../../../hooks/api/MeetingManagement";
import { DefaultLayout } from "../../../../layouts/Default";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Button } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { Main } from "../../../../themes/Default/components/Main";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Form } from "../../../../themes/Default/components/Form";
import { Row } from "../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../../../themes/Default/components/Form/components/Input";
import { Select } from "../../../../themes/Default/components/Form/components/Select";
import { List } from "../../../../themes/Default/components/List";
import { TableHeadCell } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableRow } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHead } from "../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableDataCell } from "../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { TableBody } from "../../../../themes/Default/components/List/components/Body/components/TableBody";
import { prepareSelect } from "../../../../services/core/Select";
import { Textarea } from "../../../../themes/Default/components/Form/components/Textarea";
import { Header as ListHeader } from "../../../../themes/Default/components/List/components/Header";
import { Title as ListTitle } from "../../../../themes/Default/components/List/components/Header/components/Title";
import { Body as ListBody } from "../../../../themes/Default/components/List/components/Body";
import { ColorPicker } from "../../../../themes/Default/components/Form/components/ColorPicker";
import TimePicker from "../../../../themes/Default/components/Form/components/TimePicker/TimePicker";
import DatePicker from "../../../../themes/Default/components/Form/components/DatePicker/DatePicker";
import { Toaster } from "../../../../themes/Default/components/Toaster";
import { groupsManagement } from "../../../../hooks/api/GroupsManagement";
import { roles } from "../../../../services/utils/Roles";
import { useUserToken } from "../../../../hooks/core/UserToken";

const EditEvent = () => {
    const { t } = useTranslation("common");
    const formRef = useRef(null);
    const navigate = useNavigate();
    const [formErrors, setFormErrors] = useState([]);

    const { meetingId } = useParams();
    const [participants, setParticipants] = useState([]);
    const [selectedParticipants, setSelectedParticipants] = useState([]);
    const [meeting, setMeeting] = useState([]);

    const {
        useUpdateMeetingMutation,
        useViewMeetingMutation,
        useDeleteMeetingMutation,
        useParticipantsListMutation,
        useMeetingParticipantsMutation,
        useDeleteParticipantsMutation,
        useAddParticipantsMutation,
        useCancelMeetingMutation
    } = meetingManagement;

    const [updateMeeting, { isLoading: isUpdateingMeeting }] = useUpdateMeetingMutation();
    const [viewMeeting] = useViewMeetingMutation();
    const [deleteMeeting] = useDeleteMeetingMutation();
    const [cancelMeeting] = useCancelMeetingMutation();
    const [deleteParticipant] = useDeleteParticipantsMutation();
    const [addParticipant] = useAddParticipantsMutation();
    const [participantsList] = useParticipantsListMutation();
    const [meetingParticipants] = useMeetingParticipantsMutation();
    const { useGroupsListMutation, useListGroupParticipantsMutation } = groupsManagement;
    const [listGroupParticipants] = useListGroupParticipantsMutation();
    const [viewGroups] = useGroupsListMutation();
    const [groupsList, setGroupsList] = useState([]);
    const [{ getDecoded }] = useUserToken();
    const decodedToken = getDecoded();


    const tabs = [{ title: t("calendar.editEvent"), isActive: true }];

    const onSave = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onSaved = (values) => {
        const body = { ...values };
        updateMeeting({ body, meetingId }).then((result) => {
            if (result?.error) {
                setFormErrors(result?.error?.data?.error?.violations);
                toast.error(result.error.data.error.responseMessage);
            } else {
                navigate("/calendar-featured/day-view");
                // toast.info(result?.data?.info?.responseMessage);
            }
        });
    };

    const onDelete = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        deleteMeeting(meetingId).then((result) => {
                            if (result?.error) {
                                toast.error(result.error.data.error.responseMessage);
                            } else {
                                navigate("/calendar-featured/day-view");
                            }
                        });
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    const onCancel = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        cancelMeeting({meetingId}).then((result) => {
                            if (result?.error) {
                                toast.error(result.error.data.error.responseMessage);
                            } else {
                                toast.info(result.data.info.responseMessage);
                                loadMeeting();
                            }
                        });
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    const onRemove = (participantId) => {
        deleteParticipant({ participantId, meetingId }).then((result) => {
            if (result?.error) {
                toast.error(result.error.data.error.responseMessage);
            } else {
                toast.info(result.data.info.responseMessage);
                loadAttachedParticipants();
            }
        });
    };

    const loadAttachedParticipants = () => {
        meetingParticipants(meetingId).then((result) => {
            setSelectedParticipants(result?.data?.records);
        });
    };

    const loadMeeting = () => {
        viewMeeting(meetingId).then((result) => {
            setMeeting(result?.data);
            formRef.current.setValues(result?.data);
        });
    }

    useEffect(() => {
        loadMeeting();
        participantsList().then((result) => {
            let options = [];
            result?.data?.records.forEach((participant) => {
                options.push({
                    id: participant.id,
                    name: participant.firstName + " " + participant.lastName + " " + participant.aditionalInfo,
                    firstName: participant.firstName,
                    lastName: participant.lastName,
                });
            });
            setParticipants(prepareSelect(options));
        });
        viewGroups().then((result) => {
            let options = [];
            result?.data?.records.map((group) => {
                options.push(
                    {
                        id: group.id,
                        name: group.name + ' - ' + group.languageName
                    }
                );
            })
            setGroupsList(prepareSelect(options));
        });
        loadAttachedParticipants();
    }, []);

    return (
        <DefaultLayout>
            <TopNavigation>
                <Button title={t("save")} isDisabled={meeting?.isCanceled} icon="fa-regular fa-floppy-disk" onClick={onSave}/>
                <Button title={t("delete")} isDisabled={meeting?.isCanceled} icon="fa-regular fa-trash-can" onClick={onDelete}/>
                <Button title={t("cancel")} isDisabled={meeting?.isCanceled} icon="fa-regular fa-circle-xmark" onClick={onCancel}/>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Breadcrumps>
                    <Link title={t("calendar.title")} icon="fa-calendar-lines" />
                    <Link title={t("calendar.editEvent")} isActive={true} />
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        title: "",
                        description: "",
                        date: "",
                        color: "",
                        timeTo: "",
                        timeFrom: "",
                        participants: [],
                        participantsSelect: "",
                        groupsSelect: ""
                    }}
                    onSubmit={onSaved}
                >
                    {({ values, handleChange, setFieldValue, resetForm }) => (
                        <Form>
                            <Row>
                                <Field isFullWidth={true}>
                                    <Input
                                        name="title"
                                        label={t("calendar.name")}
                                        required={true}
                                        value={values.title}
                                        onChange={handleChange}
                                        error={formErrors?.title}
                                        disabled={meeting?.isCanceled}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field isFullWidth={true}>
                                    <Textarea
                                        rows={6}
                                        name="description"
                                        label={t("calendar.description")}
                                        required={true}
                                        value={values.description}
                                        onChange={handleChange}
                                        error={formErrors?.description}
                                        disabled={meeting?.isCanceled}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <DatePicker
                                        name="date"
                                        label={t("calendar.date")}
                                        required={true}
                                        value={values.date}
                                        onChange={handleChange}
                                        error={formErrors?.date}
                                        disablePast={true}
                                        disabled={meeting?.isCanceled}
                                    />
                                </Field>
                                <Field>
                                    <ColorPicker 
                                        name="color" 
                                        label={t("noteTypes.color")} 
                                        required={true} 
                                        value={values.color} 
                                        onChange={handleChange} 
                                        disabled={meeting?.isCanceled}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <TimePicker
                                        name="timeFrom"
                                        label={t("calendar.timeFrom")}
                                        required={true}
                                        value={values.timeFrom}
                                        onChange={handleChange}
                                        error={formErrors?.timeFrom}
                                        disabled={meeting?.isCanceled}
                                    />
                                </Field>
                                <Field>
                                    <TimePicker
                                        name="timeTo"
                                        label={t("calendar.timeTo")}
                                        required={true}
                                        value={values.timeTo}
                                        onChange={handleChange}
                                        error={formErrors?.timeTo}
                                        disabled={meeting?.isCanceled}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                {(decodedToken?.roles.includes(roles.SUPER_ADMIN) ||
                                    decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES) ||
                                    decodedToken?.roles.includes(roles.DIRECTOR) ||
                                    decodedToken?.roles.includes(roles.SECRETARY) ||
                                    decodedToken?.roles.includes(roles.PROFESSOR) ||
                                    decodedToken?.roles.includes(roles.PROFESSOR_COORDINATOR) ||
                                    decodedToken?.roles.includes(roles.MARKETING_MANAGER)) ? (
                                        <>
                                            <Field>
                                                <Select
                                                    name="participants"
                                                    label={t("calendar.participants")}
                                                    required={true}
                                                    options={participants}
                                                    selectedValue={values.participants}
                                                    onChange={(event) => {
                                                        const selectedParticipantId = event.target.value;
                                                        addParticipant({ meetingId, participantId: selectedParticipantId }).then((result) => {
                                                            if (result?.error) {
                                                                toast.error(result.error.data.error.responseMessage);
                                                            } else {
                                                                toast.info(result.data.info.responseMessage);
                                                                loadAttachedParticipants();
                                                            }
                                                        });
                                                    }}
                                                    disabled={meeting?.isCanceled}
                                                />
                                            </Field>
                                        </>
                                ) : null}
                                {(decodedToken?.roles.includes(roles.SUPER_ADMIN) ||
                                    decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES) ||
                                    decodedToken?.roles.includes(roles.DIRECTOR) ||
                                    decodedToken?.roles.includes(roles.SECRETARY)) ? (
                                        <>
                                            <Field>
                                                <Select
                                                    name="groupsSelect"
                                                    label={t("groups.title")}
                                                    options={groupsList}
                                                    selectedValue={values.groupsSelect}
                                                    onChange={(event) => {
                                                        const groupId = event.target.value;

                                                        listGroupParticipants(groupId).then((result) => {
                                                            const students = result?.data?.records || [];
                                                            students.forEach(s => {
                                                                addParticipant({ meetingId, participantId: s.userId }).then((result) => {
                                                                    if (result?.error) {
                                                                        toast.error(result.error.data.error.responseMessage);
                                                                    } else {
                                                                        toast.info(result.data.info.responseMessage);
                                                                        loadAttachedParticipants();
                                                                    }
                                                                });
                                                            });
                                                        });
                                                    }}
                                                />
                                            </Field>
                                        </>
                                    ) : null}
                            </Row>
                        </Form>
                    )}
                </Formik>

                <br />
                {(decodedToken?.roles.includes(roles.SUPER_ADMIN) ||
                    decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES) ||
                    decodedToken?.roles.includes(roles.DIRECTOR) ||
                    decodedToken?.roles.includes(roles.SECRETARY) ||
                    decodedToken?.roles.includes(roles.PROFESSOR) ||
                    decodedToken?.roles.includes(roles.PROFESSOR_COORDINATOR) ||
                    decodedToken?.roles.includes(roles.MARKETING_MANAGER)) ? (
                    <>
                        <List>
                            <ListHeader>
                                <ListTitle title={t("calendar.participants")} subtitle="List of the Participants who are attached to this event" />
                            </ListHeader>
                            <ListBody>
                                <TableHead>
                                    <TableRow>
                                        <TableHeadCell>
                                            <span>{t("employees.firstName")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("employees.lastName")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {selectedParticipants?.length > 0 &&
                                        selectedParticipants?.map((selectedParticipant, index) => {
                                            return (
                                                <TableRow>
                                                    <TableDataCell>{selectedParticipant.firstName}</TableDataCell>
                                                    <TableDataCell>{selectedParticipant.lastName}</TableDataCell>
                                                    <TableDataCell>
                                                        <Button icon="fa-regular fa-trash-can" onClick={() => onRemove(selectedParticipant.id)} />
                                                    </TableDataCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </ListBody>
                        </List>
                    </>
                ) : null}
                <br />
                <br />
            </Main>
        </DefaultLayout>
    );
};

export default EditEvent;
